import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

export interface HostIcalFeed {
  id: string;
  host_id: string;
  platform: "airbnb" | "vrbo" | "booking_com" | "other";
  feed_url: string;
  is_active: boolean;
  last_synced_at: string | null;
  last_sync_status: "success" | "error" | null;
  last_sync_error: string | null;
  events_synced: number;
  created_at: string;
}

export function useIcalFeeds(hostId: string | undefined) {
  return useQuery({
    queryKey: ["ical_feeds", hostId],
    enabled: !!hostId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("host_ical_feeds")
        .select("*")
        .eq("host_id", hostId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as HostIcalFeed[];
    },
  });
}

export function useAddIcalFeed(hostId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ platform, feedUrl }: { platform: HostIcalFeed["platform"]; feedUrl: string }) => {
      if (!hostId) throw new Error("No host record found");
      const { data, error } = await supabase
        .from("host_ical_feeds")
        .insert({ host_id: hostId, platform, feed_url: feedUrl.trim() })
        .select()
        .single();
      if (error) throw error;
      return data as HostIcalFeed;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ical_feeds", hostId] });
      toast({ title: "Calendar feed added", description: "It will sync automatically within the hour." });
    },
    onError: (error: Error) => {
      const message = error.message.includes("duplicate") || error.message.includes("unique")
        ? "This calendar feed has already been added."
        : error.message;
      toast({ title: "Could not add feed", description: message, variant: "destructive" });
    },
  });
}

export function useDeleteIcalFeed(hostId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (feedId: string) => {
      const { error } = await supabase.from("host_ical_feeds").delete().eq("id", feedId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ical_feeds", hostId] });
      toast({ title: "Calendar feed removed" });
    },
    onError: (error: Error) => {
      toast({ title: "Could not remove feed", description: error.message, variant: "destructive" });
    },
  });
}
